import { UseFormReturn } from "react-hook-form";
import {
    FormSchema,
    FormValues,
    ListFieldKeys,
    ValidationRuleLevel,
} from "../../DynamicFormTypes.ts";
import {
    BackendFieldError,
    ReportFormMetaData,
    Trigger,
    TypedRule,
} from "../../../../api/ApiTypes.ts";

export interface TriggerRequest {
    schema: FormSchema;
}

export interface BackendFullValidationRequest {
    schema: FormSchema;
    values: FormValues;
    metaData: ReportFormMetaData;
}

export interface BackendFullValidationResponse {
    results: Record<string, BackendFieldError | null>;
}
export interface FormValidationAPI {
    validateFullForm: (
        request: BackendFullValidationRequest
    ) => Promise<BackendFullValidationResponse>;
}


/**
 * AggregateValidator:
 * - Frontend validations are instant and always run locally.
 * - Backend validations are snapshot-based: one batch call validates all backend fields.
 * - Dependent-field triggering is done via a guarded "wave" to prevent trigger cycles/freezes.
 */
export abstract class AggregateValidator {
    protected readonly validateApi: FormValidationAPI;
    protected readonly formSchema: FormSchema;
    protected readonly metaData: ReportFormMetaData;
    protected readonly triggers: Trigger[] = [];
    protected readonly backEndTriggers: Map<string, TypedRule[]>;
    protected readonly fieldTriggerWhatToTrigger: Map<string, ListFieldKeys[]>;

    // --- SNAPSHOT STATE ---
    // The "Report Card" containing errors for the entire form
    private cachedResults: Record<string, BackendFieldError | null> = {};
    private cacheTimestamp = 0;

    // The "Latch" to prevent simultaneous network requests (ONE batch request at a time)
    private activeFetchPromise: Promise<void> | null = null;

    // Config: How long to trust the snapshot (in ms)
    private readonly CACHE_DURATION_MS = 2000;

    // --- TRIGGER WAVE STATE (prevents cycles/freezes) ---
    /**
     * Triggering dependent fields from inside a field validator can cause:
     * - cycles (A triggers B, B triggers A)
     * - massive fanout storms (datagrid expands to hundreds of keys)
     * - nested validations in RHF
     *
     * We solve this by:
     * 1) scheduling triggers on a later tick (never await trigger() inside validate call path)
     * 2) limiting each source field to trigger once per "wave"
     * 3) preventing overlapping trigger() calls
     */
    private triggerWaveId = 0;
    private activeTriggerWave: number | null = null;
    private triggeredThisWave = new Set<string>(); // fullFieldKeys already used as trigger sources in this wave
    private triggerInFlight = false;

    // --- LOGGING ---
    // Flip this to false if you want to silence debug logs globally.
    private readonly DEBUG = false;
    private log(...args: any[]) {
        if (!this.DEBUG) return;
        // eslint-disable-next-line no-console
        console.log(...args);
    }
    private warn(...args: any[]) {
        if (!this.DEBUG) return;
        // eslint-disable-next-line no-console
        console.warn(...args);
    }
    private error(...args: any[]) {
        if (!this.DEBUG) return;
        // eslint-disable-next-line no-console
        console.error(...args);
    }

    protected constructor(
        triggers: Trigger[] = [],
        validateFunction: FormValidationAPI,
        formSchema: FormSchema,
        metaData: ReportFormMetaData
    ) {
        this.log("✅ AGG_INIT: Snapshot Validator Constructed");

        this.formSchema = formSchema;
        this.metaData = metaData;
        this.validateApi = validateFunction;
        this.triggers = triggers;

        const backend = new Map<string, TypedRule[]>();
        const fieldTriggerMap = new Map<string, ListFieldKeys[]>();

        // Build:
        // - backEndTriggers: abstractKey -> rules
        // - fieldTriggerWhatToTrigger: abstractKey -> dependent ListFieldKeys[]
        if (triggers) {
            for (const trigger of triggers) {
                const converted = this.convertListFieldKey(trigger.trigger);

                //map the rules to the trigger (if we decide to not trigger everyfield on every request, we can just feed the rules up)
                const existingRules = backend.get(converted);
                if (existingRules) {
                    trigger.typedRules.forEach((x) => existingRules.push(x));
                } else {
                    backend.set(converted, [...trigger.typedRules]);
                }
                //map the fields to other fields we need to trigger when this field is triggered
                const filteredDep = trigger.targetFields.filter(dep => {
                    const isSelf =
                        dep.fieldKey === trigger.trigger.fieldKey &&
                        dep.dataGrid === trigger.trigger.dataGrid && dep.dataGrid == null
                    return !isSelf;
                });

                const existingDeps = fieldTriggerMap.get(converted);
                if (existingDeps) {
                    filteredDep.forEach((x) => existingDeps.push(x));
                } else {
                    fieldTriggerMap.set(converted, [...filteredDep]);
                }
            }
        }

        this.backEndTriggers = backend;
        this.fieldTriggerWhatToTrigger = fieldTriggerMap;
        // Helpful diagnostics at startup
        this.log(
            "🧭 AGG_INIT: triggers backend=",
            this.backEndTriggers.size,
        );
    }


    public getTriggers(): Trigger[] {
        return this.triggers;
    }

    // -----------------------
    // Key normalization
    // -----------------------

    /**
     * Abstract key format:
     * - root field: "city"
     * - grid field: "myGrid-price" (dataGrid-fieldKey)
     */
    private convertListFieldKey(trigger: ListFieldKeys): string {
        if (trigger.dataGrid) return `${trigger.dataGrid}-${trigger.fieldKey}`;
        return trigger.fieldKey;
    }

    /**
     * Concrete fullFieldKey format:
     * - root field: "city"
     * - grid field: "myGrid.0.price"
     *
     * Normalized (abstract) form becomes:
     * - "city"
     * - "myGrid-price"
     */
    private convertFullFieldKey(fullFieldKey: string): string {
        const [dataGridOrFieldKey, _dataGridIndex, fieldKeyInDataGrid] =
            fullFieldKey.split(".");
        if (fieldKeyInDataGrid) return `${dataGridOrFieldKey}-${fieldKeyInDataGrid}`;
        return dataGridOrFieldKey;
    }

    // -----------------------
    // MAIN ENTRY POINT
    // -----------------------
    async validateBest(fullFieldKey: string, formMethods: UseFormReturn<FormValues>) {
        const normalizedKey = this.convertFullFieldKey(fullFieldKey);

        this.log("🔎 AGG_VALIDATE:", { fullFieldKey, normalizedKey });

        // 2) Snapshot Check (fast path)
        if (this.isCacheValid()) {
            this.log("📌 AGG_CACHE_HIT:", {
                fullFieldKey,
                ageMs: Date.now() - this.cacheTimestamp,
            });

            // Important: do NOT await trigger() inside a validator.
            this.scheduleTriggerWave(fullFieldKey, formMethods);

            return this.lookupInCache(fullFieldKey);
        }

        // 3) Network Fetch (Latch)
        if (!this.activeFetchPromise) {
            this.log("🌐 AGG_CACHE_MISS: starting refreshSnapshot()");
            this.activeFetchPromise = this.refreshSnapshot(formMethods).finally(() => {
                this.log("🌐 AGG_REFRESH_DONE: releasing latch");
                this.activeFetchPromise = null;
            });
        } else {
            this.log("⏳ AGG_IN_FLIGHT: awaiting existing refreshSnapshot()");
        }

        await this.activeFetchPromise;

        // Schedule dependent triggers after snapshot refresh
        this.scheduleTriggerWave(fullFieldKey, formMethods);

        // 4) Return Result from the now-fresh cache
        return this.lookupInCache(fullFieldKey);
    }

    // -----------------------
    // Trigger wave scheduling
    // -----------------------

    /**
     * Starts (or joins) a trigger wave and schedules the actual triggering on a later tick.
     * This avoids nested validation call stacks and reduces the chance of RHF lock-ups.
     */
    private scheduleTriggerWave(
        sourceFullFieldKey: string,
        formMethods: UseFormReturn<FormValues>
    ) {
        // Start a new wave if none active
        if (this.activeTriggerWave == null) {
            this.triggerWaveId += 1;
            this.activeTriggerWave = this.triggerWaveId;
            this.triggeredThisWave.clear();

            this.log("🌊 AGG_WAVE_START:", { waveId: this.activeTriggerWave });

            // Release the wave on a later tick.
            // If you find waves ending too early/late, you can adjust this to a longer timeout.
            setTimeout(() => {
                // Only release if we didn't start a newer wave since scheduling this release
                if (this.activeTriggerWave === this.triggerWaveId) {
                    this.log("🌊 AGG_WAVE_END:", { waveId: this.activeTriggerWave });
                    this.activeTriggerWave = null;
                    this.triggeredThisWave.clear();
                }
            }, 100);
        }

        const waveId = this.activeTriggerWave;

        // Schedule actual trigger work
        setTimeout(() => {
            if (waveId == null) return;
            void this.triggerFieldsInWave(sourceFullFieldKey, formMethods, waveId);
        }, 0);
    }

    /**
     * Executes triggers for dependent fields with:
     * - wave guard (source field triggers once per wave)
     * - dedupe of target keys
     * - no re-trigger of original key
     * - overlap guard (only one trigger() call at a time)
     */
    private async triggerFieldsInWave(
        sourceFullFieldKey: string,
        formMethods: UseFormReturn<FormValues>,
        waveId: number
    ) {
        // If a newer wave started, abort this work
        if (this.activeTriggerWave !== waveId) {
            this.log("🛑 AGG_WAVE_ABORT: stale wave", {
                waveId,
                active: this.activeTriggerWave,
                sourceFullFieldKey,
            });
            return;
        }

        // Prevent the same source field from triggering repeatedly in the same wave
        if (this.triggeredThisWave.has(sourceFullFieldKey)) {
            this.log("🔁 AGG_WAVE_SKIP: already triggered from source this wave", {
                waveId,
                sourceFullFieldKey,
            });
            return;
        }
        this.triggeredThisWave.add(sourceFullFieldKey);

        const normalizedKey = this.convertFullFieldKey(sourceFullFieldKey);
        const fieldsToTrigger = this.fieldTriggerWhatToTrigger.get(normalizedKey);

        if (!fieldsToTrigger || fieldsToTrigger.length === 0) {
            this.log("🚫 AGG_TRIGGER_NONE:", { normalizedKey, sourceFullFieldKey });
            return;
        }

        const values = formMethods.getValues();

        // Build concrete keys and dedupe
        const concreteKeysSet = new Set<string>();

        for (const target of fieldsToTrigger) {
            if (target.dataGrid) {
                // Data grid field => trigger for every row present in the grid
                const gridData = (values as any)[target.dataGrid];
                if (Array.isArray(gridData)) {
                    for (let index = 0; index < gridData.length; index++) {
                        concreteKeysSet.add(`${target.dataGrid}.${index}.${target.fieldKey}`);
                    }
                } else {
                    this.warn("⚠️ AGG_TRIGGER_GRID_MISSING_OR_NOT_ARRAY:", {
                        sourceFullFieldKey,
                        target,
                        gridType: typeof gridData,
                    });
                }
            } else {
                // Root field
                concreteKeysSet.add(target.fieldKey);
            }
        }

        // Avoid triggering the source field itself
        concreteKeysSet.delete(sourceFullFieldKey);

        const concreteKeys = Array.from(concreteKeysSet);

        this.log("🎯 AGG_TRIGGER_PLAN:", {
            waveId,
            sourceFullFieldKey,
            normalizedKey,
            targetsCount: concreteKeys.length,
            // Uncomment if you want to see actual keys (can be large)
            // targets: concreteKeys,
        });

        if (concreteKeys.length === 0) return;

        // Prevent overlapping trigger storms
        if (this.triggerInFlight) {
            this.warn("⛔ AGG_TRIGGER_SKIPPED: trigger already in flight", {
                waveId,
                sourceFullFieldKey,
            });
            return;
        }

        this.triggerInFlight = true;
        try {
            this.log("🚀 AGG_TRIGGER_RUN:", {
                waveId,
                count: concreteKeys.length,
                sourceFullFieldKey,
            });
            await formMethods.trigger(concreteKeys);
            this.log("✅ AGG_TRIGGER_DONE:", { waveId, sourceFullFieldKey });
        } catch (e) {
            this.error("💥 AGG_TRIGGER_ERROR:", e);
        } finally {
            this.triggerInFlight = false;
        }
    }

    // -----------------------
    // Snapshot refresh
    // -----------------------

    /**
     * Sends ONE request to validate the entire form using validateBatch.
     * It reconstructs the full list of fields -> rules based on current form values.
     */
    private async refreshSnapshot(formMethods: UseFormReturn<FormValues>) {
        try {
            const values = formMethods.getValues();

            // Construct the map for EVERY backend field currently in the form
            this.log("📦 AGG_REFRESH_REQUEST:", {
                cacheAgeMs: this.cacheTimestamp ? Date.now() - this.cacheTimestamp : null,
            });

            const request: BackendFullValidationRequest = {
                values,
                metaData: this.metaData,
                schema: this.formSchema,
            };

            const response = await this.validateApi.validateFullForm(request);

            // Update Cache
            this.cachedResults = response.results;
            this.cacheTimestamp = Date.now();

            this.log("🧾 AGG_REFRESH_RESPONSE:", {
                resultsCount: Object.keys(response.results ?? {}).length,
                timestamp: this.cacheTimestamp,
            });
        } catch (e) {
            this.error("💥 AGG_REFRESH_FAILED:", e);
            // On failure, don't crash, just clear cache so we try again next time
            this.cachedResults = {};
            this.cacheTimestamp = 0;
        }
    }


    private isCacheValid(): boolean {
        return Date.now() - this.cacheTimestamp < this.CACHE_DURATION_MS;
    }

    private lookupInCache(fullFieldKey: string): AggregateValidationObject | undefined {
        const error = this.cachedResults[fullFieldKey];

        if (error) {
            this.log("❌ AGG_CACHE_RESULT_ERROR:", {
                fullFieldKey,
                level: error.level,
            });
            return {
                level: error.level,
                message: error.error,
            };
        }

        this.log("✅ AGG_CACHE_RESULT_OK:", { fullFieldKey });
        return undefined; // No error in snapshot = Valid
    }

    // -----------------------
    // Diagnostics / cycle detection
    // -----------------------

    // -----------------------
    // Standard helper methods
    // -----------------------


    isFieldIncluded(fullFieldKey: string): boolean {
        const fieldToValidate = this.convertFullFieldKey(fullFieldKey);
        return this.backEndTriggers.has(fieldToValidate);

    }
}

export interface AggregateValidationObject {
    level?: ValidationRuleLevel | undefined;
    message?: string;
    triggerOther?: string[];
}
